import { useMemo, useRef, useState } from 'react'
import { isEmpty, isArray, isObject, orderBy } from 'lodash'
import pluralize from 'pluralize'
import {
  useFloating,
  offset,
  flip,
  shift,
  arrow,
  autoUpdate,
  useClick,
  useDismiss,
  useInteractions,
  useRole,
} from '@floating-ui/react'

// Closing the dropdown unmounts the focused element, which would drop focus to
// <body> and reset the user's tab position — return it to the trigger button,
// or to the reference element itself for triggers without one (filter pills)
export const focusDropdownTrigger = (domReference) => {
  const trigger = domReference?.querySelector('button') || domReference

  trigger?.focus({ preventScroll: true })
}

export const useDropdownFloating = ({
  placement = 'bottom-start',
  isDisabled = false,
  withArrow = false,
}) => {
  const [open, setOpen] = useState(false)
  const arrowRef = useRef(null)

  const middleware = [offset(withArrow ? 8 : 4), flip(), shift({ padding: 4 })]

  if (withArrow) {
    middleware.push(arrow({ element: arrowRef, padding: 12 }))
  }

  const floating = useFloating({
    open,
    onOpenChange: (nextOpen, event) => {
      setOpen(nextOpen)

      if (!nextOpen && event?.key === 'Escape') {
        focusDropdownTrigger(floating.refs.domReference.current)
      }
    },
    whileElementsMounted: autoUpdate,
    middleware,
    placement,
  })

  const { refs, floatingStyles, context } = floating

  const click = useClick(context, { enabled: !isDisabled })
  const dismiss = useDismiss(context)
  const role = useRole(context, { role: 'listbox' })

  const { getReferenceProps, getFloatingProps } = useInteractions([click, dismiss, role])

  return {
    refs,
    floatingStyles,
    getReferenceProps,
    getFloatingProps,
    context,
    open,
    setOpen,
    arrowRef,
  }
}

// Multi-select consumers pass `selected` as either option objects or plain
// values — resolve everything to option objects so checkbox state, counts,
// and emitted updates all agree
const normalizeSelected = (selected, options) =>
  (selected || []).map((entry) => {
    if (isObject(entry)) {
      return entry
    }
    return options.find((option) => option.value === entry) || { value: entry }
  })

export const getSelectedOption = ({
  isMultiSelect,
  selected,
  options,
  buttonLabel,
  countLabel,
  handleCreate,
}) => {
  if (isMultiSelect) {
    const hasSelections = !isEmpty(selected)

    if (hasSelections) {
      if (selected.length === 1) {
        const singleSelectedOption = options.find((option) => option.value === selected[0]?.value)
        if (singleSelectedOption?.label) {
          return singleSelectedOption.label
        }
      }
      return `${selected.length} ${countLabel ? pluralize(countLabel) : 'selected'}`
    }
    return buttonLabel
  }

  const selectedOption = options.find((option) => option.value === selected)

  if (selectedOption?.label) {
    return selectedOption.label
  }
  if (Boolean(handleCreate) && selected) {
    return selected
  }

  return buttonLabel
}

export const useSearchableDropdown = ({
  options = [],
  selected,
  isMultiSelect = false,
  selectedOptionFirst = true,
  pinnedAccessor = 'is_favorite',
  buttonLabel,
  countLabel,
  handleCreate = null,
}) => {
  const sortedOptions = useMemo(
    () =>
      orderBy(
        options,
        [
          (option) => Boolean(option[pinnedAccessor]),
          (option) => (selectedOptionFirst ? option.value === selected : true),
        ],
        ['desc', 'desc']
      ),
    [options, selected, selectedOptionFirst, pinnedAccessor]
  )

  const emptyValue = isMultiSelect ? [] : null
  const isNestedSelect = isArray(options[0]?.options)
  const flatOptions = isNestedSelect ? options.flatMap((group) => group.options) : options
  const normalizedSelected = isMultiSelect ? normalizeSelected(selected, flatOptions) : selected
  const selectedLabel = getSelectedOption({
    isMultiSelect,
    selected: normalizedSelected,
    options: flatOptions,
    buttonLabel,
    countLabel,
    handleCreate,
  })

  return { sortedOptions, emptyValue, isNestedSelect, selectedLabel, normalizedSelected }
}
